<template>
  <section class="mee-root">
    <div class="mee-shell">
      <!-- Breadcrumb -->
      <nav class="mee-crumbs" aria-label="Breadcrumb">
        <a href="#" @click.prevent="goBack">Projects</a>
        <span class="mee-crumbs__sep">/</span>
        <span class="mee-crumbs__current">
          {{ project.name || "MLS entry" }}
        </span>
        <span class="mee-crumbs__sep">/</span>
        <span class="mee-crumbs__current">
          Unit
          <template v-if="uploadForm.unit">#{{ uploadForm.unit }}</template>
        </span>
      </nav>

      <!-- Project header -->
      <header class="mee-head">
        <div class="mee-head__col">
          <div class="mee-eyebrow">
            <span class="mee-eyebrow__bar"></span>Project
          </div>
          <h1 class="mee-title">{{ project.name || "Unit entry" }}</h1>
          <div class="mee-sub" v-if="project.address">
            <span class="mee-sub__pip"></span>
            {{ project.address }}
          </div>
        </div>
        <div class="mee-head__col">
          <div class="mee-meta-label">Last updated</div>
          <div class="mee-meta-value">
            {{ project.lastUpdated || "—" }}
            <span class="mee-meta-value__muted" v-if="project.lastUpdatedBy">
              {{ project.lastUpdatedBy }}
            </span>
          </div>
        </div>
        <div class="mee-head__col">
          <div class="mee-meta-label">Entry</div>
          <div class="mee-meta-value">
            <template v-if="$route.query.entry">#{{ $route.query.entry }}</template>
            <template v-else>New</template>
            <span class="mee-meta-value__muted">
              {{ statusLabel }}
            </span>
          </div>
        </div>
      </header>

      <!-- Form card -->
      <section class="mee-card">
        <header class="mee-card-head">
          <div class="mee-card-head__title">
            <h2 class="mee-card-title">
              Unit entry
              <span class="mee-chip">
                <span class="mee-chip__hash">#</span>{{ uploadForm.unit || "—" }}
              </span>
            </h2>
            <p class="mee-card-sub">
              Log pricing, sale, and supporting documentation for this unit.
            </p>
          </div>
        </header>

        <b-form id="mls-entry-edit-form" class="mee-form" @submit.prevent="mlsEntryUpload">
          <!-- ── Floor plan ── -->
          <div class="mee-group">
            <span>Floor plan</span><span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field mee-field--span-2">
              <label>Floor plan file</label>
              <a
                v-if="prefillFile.fileExist"
                :href="prefillFile.link"
                class="mee-link-card"
                target="_blank"
                rel="noopener"
              >
                <span class="mee-link-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                </span>
                <span class="mee-link-card__text">
                  <span class="mee-link-card__title">{{ prefillFile.title }}</span>
                  <span class="mee-link-card__sub">Open existing floor plan</span>
                </span>
              </a>
              <label
                v-else
                class="mee-dropzone"
                :class="{
                  'is-dragover': dragOverFloor,
                  'has-file': !!uploadForm.floorPlan,
                }"
                for="mee-floor-plan"
                @dragenter.prevent="dragOverFloor = true"
                @dragover.prevent="dragOverFloor = true"
                @dragleave.prevent="dragOverFloor = false"
                @drop.prevent="onFileDrop($event, 'floorPlan'); dragOverFloor = false"
              >
                <span class="mee-dropzone__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </span>
                <span class="mee-dropzone__text">
                  <span class="mee-dropzone__title">
                    <template v-if="uploadForm.floorPlan">
                      {{ uploadForm.floorPlan.name }}
                    </template>
                    <template v-else>
                      <span class="mee-dropzone__link">Click to upload</span>
                      or drag a floor plan here
                    </template>
                  </span>
                  <span class="mee-dropzone__sub">
                    <template v-if="uploadForm.floorPlan">
                      {{ formatFileSize(uploadForm.floorPlan.size) }} · ready to upload
                    </template>
                    <template v-else>PDF, PNG, or JPG · floor plan image</template>
                  </span>
                </span>
                <input
                  id="mee-floor-plan"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  @change="onFileChange($event, 'floorPlan')"
                />
                <button
                  v-if="uploadForm.floorPlan"
                  type="button"
                  class="mee-dropzone__clear"
                  aria-label="Remove file"
                  @click.prevent.stop="clearFile('floorPlan')"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </label>
            </div>
          </div>

          <!-- ── Unit details ── -->
          <div class="mee-group">
            <span>Unit details</span><span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field">
              <label for="mee-plan-name">Plan name</label>
              <b-form-input
                id="mee-plan-name"
                v-model="uploadForm.planName"
                class="mee-input"
                type="text"
                placeholder="e.g. A1"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-unit">Unit number</label>
              <b-form-input
                id="mee-unit"
                v-model="uploadForm.unit"
                class="mee-input"
                type="text"
                placeholder="Unit number"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-bed">Beds</label>
              <b-form-input
                id="mee-bed"
                v-model="uploadForm.bed"
                class="mee-input"
                type="text"
                placeholder="0"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-bath">Baths</label>
              <b-form-input
                id="mee-bath"
                v-model="uploadForm.bath"
                class="mee-input"
                type="text"
                placeholder="0"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-unit-size">
                Unit size <span class="mee-opt">sq ft</span>
              </label>
              <b-form-input
                id="mee-unit-size"
                v-model="uploadForm.unitSize"
                class="mee-input"
                type="text"
                placeholder="0"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-pid">
                PID <span class="mee-opt">Optional</span>
              </label>
              <b-form-input
                id="mee-pid"
                v-model="uploadForm.pid"
                class="mee-input"
                type="text"
                placeholder="Parcel identifier"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-owner">Owner name</label>
              <b-form-input
                id="mee-owner"
                v-model="uploadForm.owner"
                class="mee-input"
                type="text"
                placeholder="Owner"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-purchaser">Purchaser name</label>
              <b-form-input
                id="mee-purchaser"
                v-model="uploadForm.purchaser"
                class="mee-input"
                type="text"
                placeholder="Purchaser"
              ></b-form-input>
            </div>
          </div>

          <!-- ── Construction & verification ──
               The four fields the backend locks down together (see
               _presale_protect_mls_construction_fields): this form and the
               enrichment API are the only writers.

               Construction status is logged per entry, so it reflects the state
               of the build at the time of this entry. Completion date, closed
               date and secret shopper are single values on the unit itself, so
               editing them here updates every entry. -->
          <div class="mee-group">
            <span>Construction &amp; verification</span>
            <span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field">
              <label for="mee-construction-status">Construction status</label>
              <b-form-select
                id="mee-construction-status"
                v-model="uploadForm.constructionStatus"
                class="mee-select"
                :options="constructionStatusOption"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-completion-date">
                Completion date <span class="mee-opt">Applies to the unit</span>
              </label>
              <b-form-datepicker
                id="mee-completion-date"
                v-model="uploadForm.completionDate"
                class="mee-datepicker"
                reset-button
              ></b-form-datepicker>
            </div>
            <div class="mee-field">
              <label for="mee-closed-date">
                Closed date <span class="mee-opt">Applies to the unit</span>
              </label>
              <b-form-datepicker
                id="mee-closed-date"
                v-model="uploadForm.closedDate"
                class="mee-datepicker"
                reset-button
              ></b-form-datepicker>
            </div>
            <div class="mee-field">
              <label for="mee-secret-shopper">
                Secret shopper <span class="mee-opt">Applies to the unit</span>
              </label>
              <b-form-select
                id="mee-secret-shopper"
                v-model="uploadForm.secretShopper"
                class="mee-select"
                :options="secretShopperOption"
              ></b-form-select>
            </div>
          </div>

          <!-- ── Specs ── -->
          <div class="mee-group">
            <span>Specs</span><span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field mee-field--span-2">
              <label>FP specs</label>
              <multiselect
                v-model="uploadForm.fp"
                class="mee-multiselect"
                :options="mlsFP"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select FP specs"
                label="name"
                track-by="value"
              >
                <template slot="selection" slot-scope="{ values, isOpen }">
                  <span
                    v-if="values.length && !isOpen"
                    class="multiselect__single"
                  >
                    {{ values.map(v => v.name).join(", ") }}
                  </span>
                </template>
              </multiselect>
            </div>
            <div class="mee-field">
              <label for="mee-orientation">View orientation</label>
              <b-form-select
                id="mee-orientation"
                v-model="uploadForm.orientation"
                class="mee-select"
                :options="mlsOrientation"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-floor-level">Floor level</label>
              <b-form-select
                id="mee-floor-level"
                v-model="uploadForm.fl"
                class="mee-select"
                :options="mlsFloorLevel"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-balcony">Balcony</label>
              <b-form-select
                id="mee-balcony"
                v-model="uploadForm.balcony"
                class="mee-select"
                :options="balconyOrientation"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-balcony-area">
                Balcony area <span class="mee-opt">sq ft</span>
              </label>
              <b-form-input
                id="mee-balcony-area"
                v-model="uploadForm.balconyArea"
                class="mee-input"
                type="text"
                placeholder="0"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-pr">Parking ref</label>
              <b-form-select
                id="mee-pr"
                v-model="uploadForm.pr"
                class="mee-select"
                :options="mlsParking"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-parking">
                Parking <span class="mee-opt">Stalls / notes</span>
              </label>
              <b-form-input
                id="mee-parking"
                v-model="uploadForm.parking"
                class="mee-input"
                type="text"
                placeholder="e.g. 1 stall"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-locker">
                Locker <span class="mee-opt">Optional</span>
              </label>
              <b-form-input
                id="mee-locker"
                v-model="uploadForm.locker"
                class="mee-input"
                type="text"
                placeholder="Locker"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-gst">
                GST included <span class="mee-opt">Yes / No / notes</span>
              </label>
              <b-form-input
                id="mee-gst"
                v-model="uploadForm.gst"
                class="mee-input"
                type="text"
                placeholder="GST included"
              ></b-form-input>
            </div>
          </div>

          <!-- ── Pricing ── -->
          <div class="mee-group">
            <span>Pricing</span><span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field">
              <label for="mee-list-price">List price</label>
              <div class="mee-affix">
                <span class="mee-affix__sym">$</span>
                <b-form-input
                  id="mee-list-price"
                  v-model="uploadForm.listPrice"
                  class="mee-input"
                  type="text"
                  inputmode="decimal"
                  placeholder="0.00"
                ></b-form-input>
              </div>
            </div>
            <div class="mee-field">
              <label for="mee-price-backup">Price backup</label>
              <b-form-select
                id="mee-price-backup"
                v-model="uploadForm.priceBackup"
                class="mee-select"
                :options="priceBackupOption"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-list-date">List date</label>
              <b-form-datepicker
                id="mee-list-date"
                v-model="uploadForm.listDate"
                class="mee-datepicker"
                reset-button
              ></b-form-datepicker>
            </div>
            <div class="mee-field">
              <label for="mee-list-price-date">List price date</label>
              <b-form-datepicker
                id="mee-list-price-date"
                v-model="uploadForm.listPriceDate"
                class="mee-datepicker"
                reset-button
              ></b-form-datepicker>
            </div>
          </div>

          <!-- ── Sale ── -->
          <div class="mee-group">
            <span>Sale</span><span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field">
              <label for="mee-status">Status</label>
              <b-form-select
                id="mee-status"
                v-model="uploadForm.status"
                class="mee-select"
                :options="mlsStatus"
              ></b-form-select>
            </div>
            <div class="mee-field">
              <label for="mee-sold-date">
                Sold date <span class="mee-opt">Optional</span>
              </label>
              <b-form-datepicker
                id="mee-sold-date"
                v-model="uploadForm.soldDate"
                class="mee-datepicker"
                reset-button
              ></b-form-datepicker>
            </div>
            <div class="mee-field">
              <label for="mee-sold-price">
                Sold price <span class="mee-opt">Optional</span>
              </label>
              <div class="mee-affix">
                <span class="mee-affix__sym">$</span>
                <b-form-input
                  id="mee-sold-price"
                  v-model="uploadForm.soldPrice"
                  class="mee-input"
                  type="text"
                  inputmode="decimal"
                  placeholder="0.00"
                ></b-form-input>
              </div>
            </div>
            <div class="mee-field">
              <label class="mee-checkbox-row">
                <b-form-checkbox-group
                  v-model="uploadForm.assignment"
                  class="mee-checkbox-group"
                >
                  <b-form-checkbox value="1">Assignment</b-form-checkbox>
                </b-form-checkbox-group>
              </label>
            </div>
            <div class="mee-field">
              <label for="mee-buyer-incentives">
                Buyer incentives <span class="mee-opt">Optional</span>
              </label>
              <b-form-input
                id="mee-buyer-incentives"
                v-model="uploadForm.buyerIncentives"
                class="mee-input"
                type="text"
                placeholder="Notes for buyer side"
              ></b-form-input>
            </div>
            <div class="mee-field">
              <label for="mee-realtor-incentives">
                Realtor incentives <span class="mee-opt">Optional</span>
              </label>
              <b-form-input
                id="mee-realtor-incentives"
                v-model="uploadForm.realtorIncentives"
                class="mee-input"
                type="text"
                placeholder="Notes for realtor side"
              ></b-form-input>
            </div>
          </div>

          <!-- ── Back-up ── -->
          <div class="mee-group">
            <span>Back-up</span><span class="mee-group__line"></span>
          </div>
          <div class="mee-grid">
            <div class="mee-field mee-field--span-2">
              <label for="mee-backup-file">
                Back-up file
                <span class="mee-opt">PDF, PNG, JPG · up to 25 MB</span>
              </label>
              <label
                class="mee-dropzone"
                :class="{
                  'is-dragover': dragOverBackup,
                  'has-file': !!uploadForm.backupfile,
                }"
                for="mee-backup-file"
                @dragenter.prevent="dragOverBackup = true"
                @dragover.prevent="dragOverBackup = true"
                @dragleave.prevent="dragOverBackup = false"
                @drop.prevent="onFileDrop($event, 'backupfile'); dragOverBackup = false"
              >
                <span class="mee-dropzone__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </span>
                <span class="mee-dropzone__text">
                  <span class="mee-dropzone__title">
                    <template v-if="uploadForm.backupfile">
                      {{ uploadForm.backupfile.name }}
                    </template>
                    <template v-else>
                      <span class="mee-dropzone__link">Click to upload</span>
                      or drag a file here
                    </template>
                  </span>
                  <span class="mee-dropzone__sub">
                    <template v-if="uploadForm.backupfile">
                      {{ formatFileSize(uploadForm.backupfile.size) }} · ready to upload
                    </template>
                    <template v-else>
                      MLS listing, signed offer, or supporting documentation.
                    </template>
                  </span>
                </span>
                <input
                  id="mee-backup-file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  @change="onFileChange($event, 'backupfile')"
                />
                <button
                  v-if="uploadForm.backupfile"
                  type="button"
                  class="mee-dropzone__clear"
                  aria-label="Remove file"
                  @click.prevent.stop="clearFile('backupfile')"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </label>
            </div>
          </div>
        </b-form>

        <footer class="mee-foot">
          <div class="mee-foot__left">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            Submit creates a versioned log entry on this unit.
          </div>
          <div class="mee-foot__actions">
            <button
              type="button"
              class="mee-btn mee-btn--ghost"
              @click="goBack"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="mls-entry-edit-form"
              class="mee-btn mee-btn--primary"
              :disabled="submitting"
            >
              {{ submitting ? "Submitting…" : "Submit entry" }}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </button>
          </div>
        </footer>
      </section>
    </div>
  </section>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormFile,
  BFormDatepicker,
} from "bootstrap-vue";
import axios from "axios";
import { BACKEND_URL } from "../variables.js";
import Multiselect from "vue-multiselect";

export default {
  components: {
    "b-form": BForm,
    "b-form-group": BFormGroup,
    "b-form-input": BFormInput,
    "b-form-select": BFormSelect,
    "b-form-checkbox": BFormCheckbox,
    "b-form-checkbox-group": BFormCheckboxGroup,
    "b-form-file": BFormFile,
    "b-form-datepicker": BFormDatepicker,
    Multiselect,
  },
  data() {
    return {
      prefillFile: { fileExist: false },
      submitting: false,
      dragOverFloor: false,
      dragOverBackup: false,
      project: {
        name: "",
        address: "",
        lastUpdated: "",
        lastUpdatedBy: "",
      },
      uploadForm: {
        planName: "",
        fp: [],
        orientation: 11105,
        unit: "",
        bed: "",
        bath: "",
        unitSize: "",
        listPrice: "",
        pr: 11107,
        locker: "",
        gst: "",
        buyerIncentives: "",
        realtorIncentives: "",
        soldDate: null,
        listDate: null,
        listPriceDate: null,
        fl: 11106,
        owner: "",
        purchaser: "",
        pid: "",
        backupfile: null,
        floorPlan: null,
        balcony: null,
        balconyArea: "",
        priceBackup: "",
        soldPrice: "",
        status: null,
        assignment: [],
        parking: "",
        constructionStatus: 23496,
        completionDate: null,
        closedDate: null,
        secretShopper: null,
      },
      mlsOrientation: [
        { text: "East", value: 11033 },
        { text: "North", value: 11030 },
        { text: "North East", value: 11035 },
        { text: "North West", value: 11034 },
        { text: "South", value: 11032 },
        { text: "South East", value: 11037 },
        { text: "South West", value: 11036 },
        { text: "West", value: 11031 },
      ],
      mlsFP: [],
      mlsParking: [],
      mlsFloorLevel: [],
      mlsStatus: [
        { text: "—", value: 11482 },
        { text: "Active", value: 8197 },
        { text: "Sold", value: 8198 },
      ],
      balconyOrientation: [
        { text: "Yes", value: "Yes" },
        { text: "No", value: "No" },
      ],
      priceBackupOption: [
        { text: "—", value: "" },
        { text: "Verified", value: "Verified" },
        { text: "Semi-Verified", value: "Semi-Verified" },
        { text: "Educated Estimated", value: "Educated Estimated" },
      ],
      // The roster comes from field_secret_shopper's allowed_values via
      // api/utc_basic_fields/options — see fetchSecretShopperOptions(). Only the
      // empty choice is known up front.
      secretShopperOption: [{ text: "—", value: "" }],
      constructionStatusOption: [
        { text: "None", value: 23496 },
        { text: "Pre-Construction", value: 2899 },
        { text: "In Process", value: 2900 },
        { text: "Excavation & Foundation", value: 3630 },
        { text: "Framing", value: 3632 },
        { text: "Interior Finishing", value: 3772 },
        { text: "Some Homes Complete", value: 2904 },
        { text: "Complete", value: 2905 },
      ],
    };
  },
  computed: {
    statusLabel() {
      const s = this.mlsStatus.find((o) => o.value == this.uploadForm.status);
      return s && s.text && s.text !== "—" ? s.text : "Draft";
    },
  },
  mounted() {
    const vm = this;

    this.fetchSecretShopperOptions();

    $.get(
      { url: BACKEND_URL + "api/get_fp_specs" },
      (data, status) => {
        if (status === "success") {
          vm.mlsFP = data.fp_specs;
          vm.mlsParking = data.parking;
          vm.mlsFloorLevel = data.floor_level;
        }
      }
    );

    $.get(
      {
        url:
          BACKEND_URL +
          "api/get_mls_by_cid_entry?cid=" +
          this.$route.query.cid +
          "&entry=" +
          this.$route.query.entry,
      },
      (data, status) => {
        if (status === "success") {
          vm.uploadForm.planName = data.plan_name;
          vm.uploadForm.fp = data.fp;
          vm.uploadForm.orientation = data.orientation;
          vm.uploadForm.unit = data.address;
          vm.uploadForm.bed = data.beds;
          vm.uploadForm.bath = data.baths;
          vm.uploadForm.unitSize = data.unit_size;
          vm.uploadForm.listPrice = data.list_price;
          vm.uploadForm.pr = data.parking_ref;
          vm.uploadForm.parking = data.parking;
          vm.uploadForm.locker = data.locker;
          vm.uploadForm.gst = data.gst;
          vm.uploadForm.buyerIncentives = data.buyer;
          vm.uploadForm.realtorIncentives = data.realtor;
          vm.uploadForm.status = data.status;
          vm.uploadForm.soldDate = data.sold_date;
          vm.uploadForm.listDate = data.list_date;
          vm.uploadForm.listPriceDate = data.list_price_date;
          vm.uploadForm.assignment = data.assignment;
          vm.uploadForm.fl = data.fl;
          vm.uploadForm.owner = data.owner;
          vm.uploadForm.pid = data.pid;
          vm.uploadForm.purchaser = data.purchaser;
          vm.uploadForm.balcony = data.balcony;
          vm.uploadForm.balconyArea = data.balconyArea;
          vm.uploadForm.priceBackup = data.priceBackup;
          vm.uploadForm.soldPrice = data.sold_price;
          vm.uploadForm.constructionStatus =
            Number(data.construction_status) || 23496;
          vm.uploadForm.completionDate = data.completion_date || null;
          vm.uploadForm.closedDate = data.close_date || null;
          vm.uploadForm.secretShopper = data.secret_shopper || "";

          if (data.project_name) vm.project.name = data.project_name;
          if (data.project_address) vm.project.address = data.project_address;
          if (data.last_updated) vm.project.lastUpdated = data.last_updated;
          if (data.last_updated_by) vm.project.lastUpdatedBy = data.last_updated_by;

          if (data.floor_plan_file) {
            vm.prefillFile.link = data.floor_plan_file.link;
            vm.prefillFile.title = data.floor_plan_file.title;
            vm.prefillFile.fileExist = true;
          }
        }
      }
    );
  },
  methods: {
    // Secret shopper is a list field whose stored value is the shopper's name, so
    // the select is built from the field's own allowed_values rather than from a
    // list restated here — a name added or corrected in Drupal shows up without a
    // front-end change. A failed fetch leaves the select with only the empty
    // choice, which is honest: it cannot offer a value the field would accept.
    async fetchSecretShopperOptions() {
      try {
        const { data } = await axios.get(
          BACKEND_URL + "api/utc_basic_fields/options"
        );
        if (data && data.status === "ok" && data.options) {
          const roster = data.options.secret_shopper || [];
          this.secretShopperOption = [
            { text: "—", value: "" },
            ...roster.map((o) => ({ text: o.name, value: o.value })),
          ];
        }
      } catch (err) {
        console.error("fetchSecretShopperOptions failed", err);
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        window.location.href = "/";
      }
    },
    onFileChange(e, key) {
      const file = e.target.files && e.target.files[0];
      this.uploadForm[key] = file || null;
    },
    onFileDrop(e, key) {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.uploadForm[key] = file;
    },
    clearFile(key) {
      this.uploadForm[key] = null;
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
      return (bytes / 1024 / 1024).toFixed(1) + " MB";
    },
    mlsEntryUpload() {
      this.submitting = true;
      let formData = new FormData();
      formData.append("planName", this.uploadForm.planName);
      let fpArray = [];
      this.uploadForm.fp.forEach((element) => fpArray.push(element.value));
      formData.append("fp", fpArray);
      formData.append("pr", this.uploadForm.pr);
      formData.append("fl", this.uploadForm.fl);
      formData.append("orientation", this.uploadForm.orientation);
      formData.append("unit", this.uploadForm.unit);
      formData.append("bed", this.uploadForm.bed);
      formData.append("bath", this.uploadForm.bath);
      formData.append("unitSize", this.uploadForm.unitSize);
      formData.append("listPrice", this.uploadForm.listPrice);
      formData.append("parking", this.uploadForm.parking);
      formData.append("locker", this.uploadForm.locker);
      formData.append("gst", this.uploadForm.gst);
      formData.append("buyerIncentives", this.uploadForm.buyerIncentives);
      formData.append("realtorIncentives", this.uploadForm.realtorIncentives);
      formData.append("status", this.uploadForm.status);
      formData.append("soldPrice", this.uploadForm.soldPrice);
      formData.append("files", this.uploadForm.floorPlan);
      formData.append("soldDate", this.uploadForm.soldDate);
      formData.append("listDate", this.uploadForm.listDate);
      formData.append("listPriceDate", this.uploadForm.listPriceDate);
      formData.append("fid", this.$route.query.fid);
      formData.append("cid", this.$route.query.cid);
      formData.append("entry", this.$route.query.entry);
      formData.append("owner", this.uploadForm.owner);
      formData.append("purchaser", this.$route.query.purchaser);
      formData.append("pid", this.uploadForm.pid);
      formData.append("backupfile", this.uploadForm.backupfile);
      formData.append("balcony", this.uploadForm.balcony);
      formData.append("balconyArea", this.uploadForm.balconyArea);
      formData.append("priceBackup", this.uploadForm.priceBackup);
      formData.append("construction_status", this.uploadForm.constructionStatus);
      formData.append("completion_date", this.uploadForm.completionDate);
      formData.append("close_date", this.uploadForm.closedDate);
      formData.append("secret_shopper", this.uploadForm.secretShopper);
      var vm = this;
      $.ajax({
        type: "POST",
        url: BACKEND_URL + "api/presale_mls_entry_edit",
        data: formData,
        processData: false,
        contentType: false,
      })
        .done(function () {
          window.location.href =
            "/type?project=" +
            vm.$route.query.project +
            "&fid=" +
            vm.$route.query.fid +
            "&sid=" +
            vm.$route.query.sid +
            "&uid=" +
            vm.$route.query.uid +
            "&tab=overview";
        })
        .always(function () {
          vm.submitting = false;
        });
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* ============================================================
   MLS Entry Edit — RPD design system
   ============================================================ */
.mee-root {
  background: var(--rpd-bg, #fafbfc);
  min-height: 100vh;
  padding: 32px 16px 64px;
  color: var(--rpd-ink, #0a1320);
  font-family: var(--rpd-font-sans, "Inter Tight", "Inter", sans-serif);
  -webkit-font-smoothing: antialiased;
}

.mee-shell {
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ---------- Breadcrumb ---------- */
.mee-crumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--rpd-muted, #5c6b80);
}
.mee-crumbs a {
  color: var(--rpd-muted, #5c6b80);
  text-decoration: none;
  transition: color 120ms ease;
}
.mee-crumbs a:hover { color: var(--rpd-ink, #0a1320); }
.mee-crumbs__sep { opacity: 0.5; }
.mee-crumbs__current { color: var(--rpd-ink, #0a1320); }

/* ---------- Project header ---------- */
.mee-head {
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-lg, 16px);
  box-shadow: var(--rpd-shadow-sm);
  padding: 24px 28px;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 28px;
}
.mee-head__col + .mee-head__col {
  border-left: 1px solid var(--rpd-line-2, #eff2f6);
  padding-left: 28px;
}

.mee-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--rpd-navy-700, #1e3753);
  margin-bottom: 10px;
}
.mee-eyebrow__bar {
  width: 22px;
  height: 3px;
  background: var(--rpd-coral, #f26a6f);
  border-radius: 2px;
}

.mee-title {
  font-family: var(--rpd-font-display, "Fraunces", serif);
  font-weight: 700;
  font-size: 1.75rem;
  line-height: 1.15;
  letter-spacing: -0.01em;
  margin: 0 0 8px;
  color: var(--rpd-ink, #0a1320);
}
.mee-sub {
  font-size: 0.875rem;
  color: var(--rpd-muted, #5c6b80);
  display: flex;
  align-items: center;
  gap: 8px;
}
.mee-sub__pip {
  width: 7px; height: 7px;
  border-radius: 999px;
  background: #1f9d55;
  box-shadow: 0 0 0 3px rgba(31, 157, 85, 0.16);
}

.mee-meta-label {
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--rpd-muted, #5c6b80);
  margin-bottom: 8px;
}
.mee-meta-value {
  font-weight: 600;
  font-size: 1.0625rem;
  color: var(--rpd-ink, #0a1320);
  line-height: 1.3;
}
.mee-meta-value__muted {
  display: block;
  font-weight: 400;
  font-size: 0.8125rem;
  color: var(--rpd-muted, #5c6b80);
  margin-top: 2px;
}

/* ---------- Card ---------- */
.mee-card {
  background: var(--rpd-white, #fff);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-lg, 16px);
  box-shadow: var(--rpd-shadow-sm);
  overflow: hidden;
}
.mee-card-head {
  padding: 22px 28px 18px;
  border-bottom: 1px solid var(--rpd-line-2, #eff2f6);
}
.mee-card-title {
  font-family: var(--rpd-font-display, "Fraunces", serif);
  font-weight: 700;
  font-size: 1.375rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  margin: 0;
  color: var(--rpd-ink, #0a1320);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.mee-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--rpd-font-mono, "JetBrains Mono", monospace);
  font-size: 0.75rem;
  font-weight: 500;
  background: var(--rpd-bg-2, #f4f6f9);
  color: var(--rpd-ink-2, #1a2638);
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-pill, 999px);
  padding: 3px 10px;
  letter-spacing: 0.02em;
}
.mee-chip__hash { color: var(--rpd-coral, #f26a6f); opacity: 0.9; }
.mee-card-sub {
  font-size: 0.875rem;
  color: var(--rpd-muted, #5c6b80);
  margin: 6px 0 0;
}

/* ---------- Form body ---------- */
.mee-form { padding: 24px 28px 8px; }

.mee-group {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--rpd-navy-800, #15273d);
  margin: 26px 0 14px;
}
.mee-group:first-of-type { margin-top: 4px; }
.mee-group__line {
  flex: 1; height: 1px;
  background: var(--rpd-line-2, #eff2f6);
}

.mee-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  row-gap: 18px;
}
.mee-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.mee-field--span-2 { grid-column: 1 / -1; }
.mee-field label {
  font-weight: 500;
  font-size: 0.8125rem;
  color: var(--rpd-ink, #0a1320);
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.2;
  margin: 0;
}
.mee-opt {
  font-weight: 400;
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
  letter-spacing: 0;
  text-transform: none;
}

/* ---------- Inputs ---------- */
.mee-input,
.mee-select,
::v-deep .mee-input .form-control,
::v-deep .mee-select .custom-select,
::v-deep .mee-select select {
  width: 100% !important;
  height: 40px !important;
  padding: 0 12px !important;
  font-family: inherit !important;
  font-size: 0.875rem !important;
  color: var(--rpd-ink, #0a1320) !important;
  background: var(--rpd-white, #fff) !important;
  border: 1px solid var(--rpd-line, #e3e8ef) !important;
  border-radius: var(--rpd-r-sm, 6px) !important;
  transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  box-shadow: none !important;
}
::v-deep .mee-input .form-control::placeholder {
  color: #9aa3b1 !important;
}
::v-deep .mee-input .form-control:hover,
::v-deep .mee-select .custom-select:hover,
::v-deep .mee-select select:hover {
  border-color: var(--rpd-navy-400, #5a7795) !important;
}
::v-deep .mee-input .form-control:focus,
::v-deep .mee-select .custom-select:focus,
::v-deep .mee-select select:focus {
  outline: none !important;
  border-color: var(--rpd-navy-800, #15273d) !important;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.18) !important;
}

/* Select chevron */
::v-deep .mee-select .custom-select,
::v-deep .mee-select select {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230E1B2C' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>") !important;
  background-repeat: no-repeat !important;
  background-position: right 10px center !important;
  padding-right: 32px !important;
}

/* Datepicker (bootstrap-vue wraps a button + input) */
::v-deep .mee-datepicker {
  height: 40px;
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-white, #fff);
  padding: 0 4px 0 0;
  font-size: 0.875rem;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
::v-deep .mee-datepicker:hover { border-color: var(--rpd-navy-400, #5a7795); }
::v-deep .mee-datepicker.focus,
::v-deep .mee-datepicker:focus-within {
  border-color: var(--rpd-navy-800, #15273d);
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.18);
}
::v-deep .mee-datepicker .btn {
  border: none !important;
  background: transparent !important;
  color: var(--rpd-ink, #0a1320) !important;
  box-shadow: none !important;
}
::v-deep .mee-datepicker label {
  color: var(--rpd-ink, #0a1320);
  font-size: 0.875rem;
  padding: 0 8px;
}
::v-deep .mee-datepicker output { color: var(--rpd-ink, #0a1320); }

/* $ affix */
.mee-affix { position: relative; display: flex; }
.mee-affix__sym {
  position: absolute;
  left: 12px; top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--rpd-muted, #5c6b80);
  pointer-events: none;
  z-index: 1;
}
.mee-affix ::v-deep .form-control {
  padding-left: 24px !important;
  font-variant-numeric: tabular-nums;
}

/* Multiselect overrides */
::v-deep .mee-multiselect .multiselect__tags {
  min-height: 40px;
  padding: 6px 40px 0 10px;
  border-radius: var(--rpd-r-sm, 6px);
  border: 1px solid var(--rpd-line, #e3e8ef);
  background: var(--rpd-white, #fff);
  font-size: 0.875rem;
}
::v-deep .mee-multiselect .multiselect__tag {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  border-radius: var(--rpd-r-sm, 6px);
  padding: 4px 22px 4px 8px;
  font-size: 0.75rem;
}
::v-deep .mee-multiselect .multiselect__tag-icon:after { color: #fff; }
::v-deep .mee-multiselect .multiselect__option--highlight {
  background: var(--rpd-navy-800, #15273d);
}
::v-deep .mee-multiselect .multiselect__option--selected.multiselect__option--highlight {
  background: var(--rpd-coral, #f26a6f);
}
::v-deep .mee-multiselect .multiselect__placeholder {
  color: #9aa3b1;
  padding: 0;
  margin: 0;
  font-size: 0.875rem;
}
::v-deep .mee-multiselect .multiselect__single {
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  margin: 0;
  padding: 0;
}
::v-deep .mee-multiselect.multiselect--active .multiselect__tags {
  border-color: var(--rpd-navy-800, #15273d);
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.18);
}

/* Checkbox row */
.mee-checkbox-row {
  height: 40px;
  display: flex !important;
  align-items: center;
  padding-left: 2px;
}
::v-deep .mee-checkbox-group {
  display: flex;
  align-items: center;
}
::v-deep .mee-checkbox-group .custom-control-label {
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  cursor: pointer;
  padding-left: 4px;
}
::v-deep .mee-checkbox-group .custom-control-input:checked ~ .custom-control-label::before {
  background-color: var(--rpd-navy-800, #15273d);
  border-color: var(--rpd-navy-800, #15273d);
}

/* ---------- Existing floor plan link card ---------- */
.mee-link-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-md, 10px);
  background: var(--rpd-bg-2, #f4f6f9);
  color: inherit;
  text-decoration: none;
  transition: border-color 120ms ease, background 120ms ease;
}
.mee-link-card:hover {
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-white, #fff);
  text-decoration: none;
}
.mee-link-card__icon {
  width: 38px; height: 38px;
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-white, #fff);
  color: var(--rpd-navy-800, #15273d);
  display: grid; place-items: center;
  flex-shrink: 0;
  border: 1px solid var(--rpd-line, #e3e8ef);
}
.mee-link-card__icon svg { width: 20px; height: 20px; stroke-width: 1.75; }
.mee-link-card__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.mee-link-card__title {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mee-link-card__sub {
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
}

/* ---------- Dropzone ---------- */
.mee-dropzone {
  position: relative;
  border: 1.5px dashed var(--rpd-line, #e3e8ef);
  border-radius: var(--rpd-r-md, 10px);
  background: var(--rpd-bg, #fafbfc);
  padding: 16px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 0;
  transition: border-color 120ms ease, background 120ms ease;
}
.mee-dropzone:hover {
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-bg-2, #f4f6f9);
}
.mee-dropzone.is-dragover {
  border-color: var(--rpd-coral, #f26a6f);
  background: #fff4f4;
}
.mee-dropzone.has-file {
  border-style: solid;
  border-color: var(--rpd-navy-700, #1e3753);
  background: var(--rpd-bg-2, #f4f6f9);
}
.mee-dropzone__icon {
  width: 38px; height: 38px;
  border-radius: var(--rpd-r-sm, 6px);
  background: var(--rpd-bg-2, #f4f6f9);
  color: var(--rpd-navy-800, #15273d);
  display: grid; place-items: center;
  flex-shrink: 0;
}
.mee-dropzone__icon svg { width: 20px; height: 20px; stroke-width: 1.75; }
.mee-dropzone__text {
  display: flex; flex-direction: column; gap: 2px;
  flex: 1; min-width: 0; overflow: hidden;
}
.mee-dropzone__title {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--rpd-ink, #0a1320);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mee-dropzone__link {
  color: var(--rpd-navy-700, #1e3753);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.mee-dropzone__sub {
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
}
.mee-dropzone input[type="file"] {
  position: absolute;
  width: 1px; height: 1px;
  opacity: 0; pointer-events: none;
}
.mee-dropzone__clear {
  appearance: none;
  background: transparent;
  border: 1px solid var(--rpd-line, #e3e8ef);
  color: var(--rpd-muted, #5c6b80);
  width: 28px; height: 28px;
  border-radius: var(--rpd-r-sm, 6px);
  cursor: pointer;
  display: grid; place-items: center;
  flex-shrink: 0;
  transition: background 120ms ease, color 120ms ease;
}
.mee-dropzone__clear:hover {
  background: var(--rpd-white, #fff);
  color: var(--rpd-ink, #0a1320);
}
.mee-dropzone__clear svg { width: 14px; height: 14px; stroke-width: 1.75; }

/* ---------- Footer ---------- */
.mee-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 18px 28px;
  margin-top: 8px;
  border-top: 1px solid var(--rpd-line-2, #eff2f6);
  background: var(--rpd-bg, #fafbfc);
  flex-wrap: wrap;
}
.mee-foot__left {
  font-size: 0.75rem;
  color: var(--rpd-muted, #5c6b80);
  display: flex; align-items: center; gap: 8px;
}
.mee-foot__left svg { width: 14px; height: 14px; stroke-width: 1.75; }
.mee-foot__actions { display: flex; gap: 10px; }

.mee-btn {
  appearance: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 10px 18px;
  border-radius: var(--rpd-r-sm, 6px);
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 120ms ease, color 120ms ease,
    border-color 120ms ease, box-shadow 120ms ease;
}
.mee-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
}
.mee-btn[disabled] { opacity: 0.65; cursor: not-allowed; }
.mee-btn svg { width: 16px; height: 16px; stroke-width: 1.75; }
.mee-btn--ghost {
  background: transparent;
  color: var(--rpd-ink, #0a1320);
  border-color: var(--rpd-line, #e3e8ef);
}
.mee-btn--ghost:hover {
  background: var(--rpd-white, #fff);
  border-color: var(--rpd-navy-700, #1e3753);
}
.mee-btn--primary {
  background: var(--rpd-navy-800, #15273d);
  color: #fff;
  border-color: var(--rpd-navy-800, #15273d);
}
.mee-btn--primary:hover {
  background: var(--rpd-navy-700, #1e3753);
  border-color: var(--rpd-navy-700, #1e3753);
}
.mee-btn--primary:active {
  background: var(--rpd-navy-900, #0e1b2c);
}

/* ---------- Responsive ---------- */
@media (max-width: 880px) {
  .mee-head {
    grid-template-columns: 1fr;
    padding: 20px 20px;
    gap: 18px;
  }
  .mee-head__col + .mee-head__col {
    border-left: 0;
    padding-left: 0;
    border-top: 1px solid var(--rpd-line-2, #eff2f6);
    padding-top: 18px;
  }
}
@media (max-width: 720px) {
  .mee-root { padding: 20px 12px 48px; }
  .mee-card-head { padding: 18px 18px 14px; }
  .mee-form { padding: 18px 18px 6px; }
  .mee-foot { padding: 16px 18px; }
  .mee-grid { grid-template-columns: 1fr; column-gap: 0; }
  .mee-foot__actions { width: 100%; }
  .mee-btn { flex: 1; justify-content: center; }
  .mee-title { font-size: 1.5rem; }
}

/* ============================================================
   Dark mode
   ============================================================ */
html.dark .mee-root {
  background: var(--rpd-navy-900, #0e1b2c);
  color: #e6ecf3;
}
html.dark .mee-crumbs,
html.dark .mee-crumbs a { color: #8a96a8; }
html.dark .mee-crumbs a:hover,
html.dark .mee-crumbs__current { color: #f0f4f9; }

html.dark .mee-head,
html.dark .mee-card {
  background: #0f1d30;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
html.dark .mee-head__col + .mee-head__col,
html.dark .mee-card-head,
html.dark .mee-foot {
  border-color: rgba(255, 255, 255, 0.06);
}
html.dark .mee-title,
html.dark .mee-meta-value,
html.dark .mee-card-title { color: #fff; }
html.dark .mee-sub,
html.dark .mee-meta-label,
html.dark .mee-meta-value__muted,
html.dark .mee-card-sub,
html.dark .mee-foot__left { color: #8a96a8; }
html.dark .mee-eyebrow { color: #c4d3e6; }
html.dark .mee-chip {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.10);
  color: #e6ecf3;
}
html.dark .mee-group { color: #c8d3e0; }
html.dark .mee-group__line { background: rgba(255, 255, 255, 0.08); }
html.dark .mee-field label { color: #e6ecf3; }
html.dark .mee-opt { color: #8a96a8; }

html.dark ::v-deep .mee-input .form-control,
html.dark ::v-deep .mee-select .custom-select,
html.dark ::v-deep .mee-select select {
  background: rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(255, 255, 255, 0.10) !important;
  color: #f0f4f9 !important;
}
html.dark ::v-deep .mee-input .form-control::placeholder { color: #7a8595 !important; }
html.dark ::v-deep .mee-input .form-control:hover,
html.dark ::v-deep .mee-select .custom-select:hover,
html.dark ::v-deep .mee-select select:hover {
  border-color: rgba(255, 255, 255, 0.22) !important;
}
html.dark ::v-deep .mee-input .form-control:focus,
html.dark ::v-deep .mee-select .custom-select:focus,
html.dark ::v-deep .mee-select select:focus {
  border-color: #fff !important;
  box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.35) !important;
}
html.dark ::v-deep .mee-select .custom-select,
html.dark ::v-deep .mee-select select {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23E6ECF3' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>") !important;
}

html.dark ::v-deep .mee-datepicker {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
}
html.dark ::v-deep .mee-datepicker .btn { color: #f0f4f9 !important; }
html.dark ::v-deep .mee-datepicker label,
html.dark ::v-deep .mee-datepicker output { color: #f0f4f9; }

html.dark .mee-affix__sym { color: #8a96a8; }

html.dark ::v-deep .mee-multiselect .multiselect__tags {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
  color: #f0f4f9;
}
html.dark ::v-deep .mee-multiselect .multiselect__placeholder { color: #7a8595; }
html.dark ::v-deep .mee-multiselect .multiselect__single {
  background: transparent;
  color: #f0f4f9;
}
html.dark ::v-deep .mee-multiselect .multiselect__content-wrapper {
  background: #0f1d30;
  border-color: rgba(255, 255, 255, 0.10);
}
html.dark ::v-deep .mee-multiselect .multiselect__option { color: #e6ecf3; }

html.dark ::v-deep .mee-checkbox-group .custom-control-label { color: #e6ecf3; }
html.dark ::v-deep .mee-checkbox-group .custom-control-label::before {
  background-color: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.22);
}

html.dark .mee-link-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
}
html.dark .mee-link-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .mee-link-card__icon {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.10);
  color: #fff;
}
html.dark .mee-link-card__title { color: #f0f4f9; }
html.dark .mee-link-card__sub { color: #8a96a8; }

html.dark .mee-dropzone {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .mee-dropzone:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .mee-dropzone.has-file {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.20);
}
html.dark .mee-dropzone__icon {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
html.dark .mee-dropzone__title { color: #f0f4f9; }
html.dark .mee-dropzone__link { color: #c4d3e6; }
html.dark .mee-dropzone__sub { color: #8a96a8; }
html.dark .mee-dropzone__clear {
  border-color: rgba(255, 255, 255, 0.12);
  color: #b6c0cf;
}
html.dark .mee-foot {
  background: rgba(255, 255, 255, 0.02);
}
html.dark .mee-btn--ghost {
  color: #e6ecf3;
  border-color: rgba(255, 255, 255, 0.12);
}
html.dark .mee-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.22);
}
html.dark .mee-btn--primary {
  background: #fff;
  color: var(--rpd-navy-900, #0e1b2c);
  border-color: #fff;
}
html.dark .mee-btn--primary:hover {
  background: #e6ecf3;
  border-color: #e6ecf3;
}
</style>
